import { mapGetters } from "vuex";
import utils from "../../../utils/util";
import pageheader from "../../pageheader";
import CustomerScopeNotice from "@/components/Common/CustomerScopeNotice";
import rules from "../../../utils/rules";
import telematicsService from "../../../api/telematics.service";

// BE-1121: the Birch master code used to be an unlisted Admin screen and the
// per-customer setup was a block buried on the customer record. Both are here,
// so turning access control on for a customer no longer means leaving the
// section. The customer half follows the toolbar customer selector.
export default {
  name: "customer-access-setup",
  components: { pageheader, CustomerScopeNotice },
  data() {
    return {
      input: {},
      rules: rules,

      // Birch master code (customerId 0)
      masterName: "",
      masterCode: "",
      masterEnabled: false,
      masterIsEdit: false,
      masterId: "",
      masterLoading: false,
      isRefreshLoading: false,

      // selected customer's access control
      customerName: "",
      customerCode: "",
      customerEnabled: false,
      customerIsEdit: false,
      customerAccessId: "",
      customerLoading: false,
      allRentedFleet: true,
      confirmationDialog: false,
      accessVersion: null,
      accessCreatedBy: "",
      accessCreatedDate: null,
      // last saved values, restored when the toggle is switched back on
      savedCustomerName: "",
      savedCustomerCode: "",
      savedAllRentedFleet: true,
    };
  },
  computed: {
    ...mapGetters(["roles", "companyIdDropdown", "companyNameDropdown"]),
    isAdmin() {
      return this.roles == "Admin";
    },
    hasCustomerSelected() {
      return Boolean(this.companyIdDropdown);
    },
    customerSaveDisabled() {
      // Stricter than the customer record was: that rule allowed saving an
      // enabled record with both fields empty, which stores an enabled access
      // code with no code.
      return (
        this.customerEnabled &&
        (this.customerName == "" || String(this.customerCode || "").length <= 3)
      );
    },
  },
  watch: {
    companyIdDropdown() {
      this.resetCustomerForm();
      if (this.hasCustomerSelected) {
        this.getCustomerKey();
      }
    },
  },
  mounted() {
    this.input = utils.getInputContent("accesscontrolsetup", this.roles);
    if (this.isAdmin) {
      this.getMasterKey();
    }
    if (this.hasCustomerSelected) {
      this.getCustomerKey();
    }
  },
  methods: {
    // ---- Birch master code -------------------------------------------------
    async getMasterKey() {
      try {
        let response = await telematicsService.getMasterKey(0);
        if (response && response.data) {
          this.masterIsEdit = true;
          this.masterId = response.data.id;
          this.masterName = response.data.description;
          this.masterCode = response.data.code;
          this.masterEnabled = response.data.isActive;
        }
      } catch (err) {
        console.log(err);
      }
    },
    masterPayload() {
      return {
        code: this.masterEnabled ? this.masterCode : "",
        description: this.masterEnabled ? this.masterName : "",
        masterKeyType: "BIRCH_MASTER_KEY",
        isEnabled: this.masterEnabled,
        customerId: 0,
      };
    },
    async saveMasterKey() {
      this.masterLoading = true;
      try {
        let response = this.masterIsEdit
          ? await telematicsService.updateAccessKeys(
              this.masterId,
              this.masterPayload()
            )
          : await telematicsService.saveAccessKeys(this.masterPayload());
        if (response) {
          this.$toast.success(response.systemMessage);
          this.getMasterKey();
        }
      } catch (err) {
        console.log(err);
      }
      this.masterLoading = false;
    },
    changeMasterEnabled() {
      if (!this.masterEnabled) {
        this.masterName = "";
        this.masterCode = "";
      }
    },
    async refreshAccesscodes() {
      this.isRefreshLoading = true;
      try {
        let response = await telematicsService.getAccesscodesRefresh();
        if (response.isSuccess) {
          this.$toast.success(response.systemMessage);
        }
      } catch (err) {
        console.log(err);
      }
      this.isRefreshLoading = false;
    },

    // ---- selected customer -------------------------------------------------
    resetCustomerForm() {
      this.customerName = "";
      this.customerCode = "";
      // These three back the "restore what was there" behaviour of the Enable
      // toggle. They MUST be cleared with the rest: left behind, switching
      // customer and toggling Enable pre-fills the previous customer's access
      // code and saves it against this one.
      this.savedCustomerName = "";
      this.savedCustomerCode = "";
      this.savedAllRentedFleet = true;
      this.customerEnabled = false;
      this.customerIsEdit = false;
      this.customerAccessId = "";
      this.allRentedFleet = true;
      this.accessVersion = null;
      this.accessCreatedBy = "";
      this.accessCreatedDate = null;
    },
    async getCustomerKey() {
      // The toolbar can change again while this is in flight; a late response
      // for the previous customer would otherwise set customerAccessId to their
      // record and the next save would rewrite it.
      const requestedFor = this.companyIdDropdown;
      try {
        let response = await telematicsService.getMasterKey(requestedFor);
        if (requestedFor !== this.companyIdDropdown) return;
        if (response && response.data) {
          this.customerIsEdit = true;
          this.customerAccessId = response.data.id;
          this.customerName = response.data.description;
          this.customerCode = response.data.code;
          // BE-1066: coerce to a strict boolean so a missing flag never leaves
          // the toggle stuck on its default-true and misrepresents the record.
          this.allRentedFleet = response.data.allRentedFleet === true;
          // Carried over from the customer record as-is: the API reports "on"
          // through isSuccess on this call rather than a field on data, unlike
          // the master key above which reads data.isActive.
          this.customerEnabled = response.isSuccess;
          this.savedCustomerName = response.data.description;
          this.savedCustomerCode = response.data.code;
          this.savedAllRentedFleet = response.data.allRentedFleet === true;
          this.accessVersion = response.data.version;
          this.accessCreatedBy = response.data.createdBy;
          this.accessCreatedDate = response.data.createdDate;
        }
      } catch (err) {
        console.log(err);
      }
    },
    changeCustomerEnabled() {
      if (this.customerEnabled) {
        this.customerName = this.savedCustomerName;
        this.customerCode = this.savedCustomerCode;
        this.allRentedFleet = this.savedAllRentedFleet;
      } else {
        this.customerName = "";
        this.customerCode = "";
        this.allRentedFleet = true;
      }
    },
    // Switching access control off deletes the customer's keys in Trackunit, so
    // it is confirmed first -- same guard the customer record had.
    saveCustomerKeys() {
      if (!this.customerEnabled) {
        this.confirmationDialog = true;
      } else {
        this.handleCustomerKeys();
      }
    },
    async handleCustomerKeys() {
      this.customerLoading = true;
      try {
        let payload = {
          code: this.customerEnabled ? this.customerCode : "",
          description: this.customerEnabled ? this.customerName : "",
          masterKeyType: "CUSTOMER_MASTER_KEY",
          isEnabled: this.customerEnabled,
          customerId: this.companyIdDropdown,
          customerName: this.companyNameDropdown,
          allRentedFleet: this.allRentedFleet,
        };
        let response = this.customerIsEdit
          ? await telematicsService.updateAccessKeys(
              this.customerAccessId,
              payload
            )
          : await telematicsService.saveAccessKeys(payload);
        if (response) {
          this.$toast.success(response.systemMessage);
          this.getCustomerKey();
        }
      } catch (err) {
        console.log(err);
      }
      this.customerLoading = false;
    },
    formatAuditDate(value) {
      if (!value) return "";
      const d = new Date(value);
      return isNaN(d.getTime()) ? "" : d.toLocaleString();
    },
  },
};
